// components/market/ShopHeader.tsx
//
// Header affiché en haut de la page boutique complète
// (app/(tabs)/market/boutiques/[slug].tsx). Respecte les toggles
// ui_config.header (show_tagline, show_description, show_phone,
// show_whatsapp_business, show_address, show_email) via useMarketTheme().
//
// PATCH V13 : le bandeau de couverture était un placeholder statique
// (View + icône Ionicons), jamais relié à shop.cover_image_url. Ajout
// d'un vrai <Image> quand l'URL existe, avec repli sur le placeholder
// Ionicons d'origine (fond violet clair + icône storefront) si
// cover_image_url est absente ou si le chargement échoue (onError).
//
// Rappel (voir échange précédent) : si des champs comme description,
// contact_phone, address, contact_email ou whatsapp_business_phone ne
// s'affichent pas malgré des données présentes côté useShop, la cause
// la plus probable est ui_config.header (show_description, show_phone,
// show_address, show_email, show_whatsapp_business) réglé à false pour
// cette boutique — pas un bug de ce composant.
//
// PATCH i18n : les libellés fixes (Produits, Abonnés, Abonné/S'abonner)
// passent par react-i18next (namespace market.components.shopHeader).
// shop.name, shop.tagline, shop.description et les coordonnées viennent
// du backend (saisies par le vendeur) et restent non traduits.

import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Linking, Image } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { Platform } from 'react-native';
import { useTranslation } from 'react-i18next';

import { useMarketTheme } from '@/components/market/ThemeProvider';
import type { MarketShopListItem } from '@/hooks/market/useShopsList';

const NATIVE = Platform.OS !== 'web';

// Le détail complet d'une boutique (get-shop) a plus de champs que la
// liste — on type ici les champs supplémentaires utilisés par le
// header, en étendant MarketShopListItem.
export type MarketShopDetail = MarketShopListItem & {
  description: string | null;
  whatsapp_business_phone: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  address: string | null;
};

type ShopHeaderProps = {
  shop: MarketShopDetail;
  isSubscribed: boolean;
  onToggleSubscribe: () => void;
  togglingSubscribe?: boolean;
};

export default function ShopHeader({ shop, isSubscribed, onToggleSubscribe, togglingSubscribe }: ShopHeaderProps) {
  const { t } = useTranslation();
  const { config } = useMarketTheme();
  const headerConfig = config.header;

  // Repli sur le placeholder si l'URL est absente OU si le chargement
  // de l'image échoue (lien cassé, image supprimée côté storage, etc.).
  const [imageFailed, setImageFailed] = useState(false);
  const showCoverImage = !!shop.cover_image_url && !imageFailed;

  const handleWhatsApp = () => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    if (!shop.whatsapp_business_phone) return;
    const cleanPhone = shop.whatsapp_business_phone.replace(/[^\d]/g, '');
    Linking.openURL(`https://wa.me/${cleanPhone}`);
  };

  const handleToggleSubscribe = () => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    onToggleSubscribe();
  };

  return (
    <View style={styles.container}>
      {/* ── Bandeau image ── */}
      {showCoverImage ? (
        <Image
          source={{ uri: shop.cover_image_url }}
          style={styles.coverImage}
          resizeMode="cover"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <View style={styles.coverPlaceholder}>
          <Ionicons name="storefront" size={40} color="#C4B5FD" />
        </View>
      )}

      <View style={styles.content}>
        {/* ── Nom + badge vérifié ── */}
        <View style={styles.nameRow}>
          <Text style={styles.name}>{shop.name}</Text>
          {shop.is_verified && (
            <Ionicons name="checkmark-circle" size={18} color={config.colors.primary} style={{ marginLeft: 6 }} />
          )}
        </View>

        {/* ── Tagline ── */}
        {headerConfig.show_tagline && !!shop.tagline && (
          <Text style={styles.tagline}>{shop.tagline}</Text>
        )}

        {/* ── Description ── */}
        {headerConfig.show_description && !!shop.description && (
          <Text style={styles.description}>{shop.description}</Text>
        )}

        {/* ── Compteurs ── */}
        <View style={styles.statsRow}>
          <View style={styles.statItem}>
            <Text style={styles.statValue}>{shop.total_products}</Text>
            <Text style={styles.statLabel}>{t('market.components.shopHeader.productsLabel')}</Text>
          </View>
          <View style={styles.statDivider} />
          <View style={styles.statItem}>
            <Text style={styles.statValue}>{shop.subscriber_count}</Text>
            <Text style={styles.statLabel}>{t('market.components.shopHeader.subscribersLabel')}</Text>
          </View>
        </View>

        {/* ── Bloc contact ── */}
        {(headerConfig.show_phone && !!shop.contact_phone) ||
        (headerConfig.show_address && !!shop.address) ||
        (headerConfig.show_email && !!shop.contact_email) ? (
          <View style={styles.contactBlock}>
            {headerConfig.show_phone && !!shop.contact_phone && (
              <View style={styles.contactRow}>
                <Ionicons name="call-outline" size={14} color="#999" />
                <Text style={styles.contactText}>{shop.contact_phone}</Text>
              </View>
            )}
            {headerConfig.show_address && !!shop.address && (
              <View style={styles.contactRow}>
                <Ionicons name="location-outline" size={14} color="#999" />
                <Text style={styles.contactText}>{shop.address}</Text>
              </View>
            )}
            {headerConfig.show_email && !!shop.contact_email && (
              <View style={styles.contactRow}>
                <Ionicons name="mail-outline" size={14} color="#999" />
                <Text style={styles.contactText}>{shop.contact_email}</Text>
              </View>
            )}
          </View>
        ) : null}

        {/* ── Boutons d'action ── */}
        <View style={styles.actionsRow}>
          <TouchableOpacity
            style={[
              styles.subscribeBtn,
              { backgroundColor: isSubscribed ? '#F3F4F6' : config.colors.primary },
            ]}
            activeOpacity={0.8}
            onPress={handleToggleSubscribe}
            disabled={togglingSubscribe}
          >
            <Ionicons
              name={isSubscribed ? 'checkmark' : 'add'}
              size={16}
              color={isSubscribed ? '#374151' : '#fff'}
            />
            <Text style={[styles.subscribeBtnText, { color: isSubscribed ? '#374151' : '#fff' }]}>
              {togglingSubscribe
                ? t('market.components.shopHeader.subscribing')
                : isSubscribed
                  ? t('market.components.shopHeader.subscribed')
                  : t('market.components.shopHeader.subscribe')}
            </Text>
          </TouchableOpacity>

          {headerConfig.show_whatsapp_business && !!shop.whatsapp_business_phone && (
            <TouchableOpacity style={styles.whatsappBtn} activeOpacity={0.8} onPress={handleWhatsApp}>
              <Ionicons name="logo-whatsapp" size={16} color="#25D366" />
            </TouchableOpacity>
          )}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
  },
  coverImage: {
    width: '100%',
    height: 140,
    backgroundColor: '#F3E8FF',
  },
  coverPlaceholder: {
    width: '100%',
    height: 140,
    backgroundColor: '#F3E8FF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    padding: 16,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  name: {
    fontSize: 20,
    fontWeight: '800',
    color: '#222',
  },
  tagline: {
    fontSize: 14,
    color: '#666',
    marginTop: 4,
  },
  description: {
    fontSize: 13,
    color: '#666',
    marginTop: 8,
    lineHeight: 19,
  },
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 14,
    gap: 16,
  },
  statItem: {
    alignItems: 'center',
  },
  statValue: {
    fontSize: 16,
    fontWeight: '800',
    color: '#222',
  },
  statLabel: {
    fontSize: 11,
    color: '#999',
    marginTop: 1,
  },
  statDivider: {
    width: 1,
    height: 24,
    backgroundColor: '#E5E7EB',
  },
  contactBlock: {
    marginTop: 14,
    gap: 6,
  },
  contactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  contactText: {
    fontSize: 12.5,
    color: '#666',
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: 16,
  },
  subscribeBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 11,
    borderRadius: 10,
  },
  subscribeBtnText: {
    fontSize: 14,
    fontWeight: '700',
  },
  whatsappBtn: {
    width: 42,
    height: 42,
    borderRadius: 10,
    backgroundColor: '#F0FDF4',
    justifyContent: 'center',
    alignItems: 'center',
  },
});
